import React, { useEffect, useMemo, useState } from "react";
import Axios from "axios";
import { Auth } from "aws-amplify";

import {
  Col, Row,
} from 'src/components/Component';
import { CATEGORIES } from "src/config/shield-rules";
import StatBox from "src/pages/dashboard/pages/main/components/statistics/stat-box";
import Head from "src/layout/head/Head";
import { API_ROUTES } from "src/config/api";
import Skeleton from "react-loading-skeleton";

import { useSelector } from "react-redux";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

const RulesSection = ({ rules, updateRule, category, loading, organisationRules }) => {
  const filtered = useMemo(() => {
    return rules.filter((r) => r.category === category);
  }, [rules, category]);

  if (loading === true) {
    return (
      <div className="overflow-hidden bg-white border rounded-sm mb-4">
        <div className="bg-slate-50 px-4 py-2">
          <h2 className="text-xl text-violet-800">{CATEGORIES.find((c) => c.id === category)?.label || category} <span className="font-bold">Rules</span></h2>
        </div>
        <div className="py-2 mx-6">
          <Skeleton count={5} height="50px" style={{ marginBottom: '8px' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden bg-white border rounded-sm mb-4">
      <div className="bg-slate-50 px-4 py-2">
        <h2 className="text-xl text-violet-800">{CATEGORIES.find((c) => c.id === category)?.label || category} <span className="font-bold">Rules</span></h2>
      </div>

      <div className="grid grid-cols-12 gap-2 px-4 pt-1 pb-4 text-sm font-mono uppercase tracking-wider text-violet-800 bg-violet-200 font-bold ">
        <div className="col-span-1">ID</div>
        <div className="col-span-7 flex items-center gap-3">
          Rule
        </div>
        <div className="col-span-2">Parameter</div>
        <div className="col-span-2 text-right pr-1">Enabled</div>
      </div>

      <ul className="divide-y divide-gray-100">
        {filtered.map((r) => (
          <li key={r.id} className="pt-3 pb-3 overflow-hidden h-auto">
            <div className="grid grid-cols-12 gap-2 px-4 items-center h-auto mb-2">
              <div className="col-span-1">
                <span className="inline-flex items-center rounded-xl border px-2 py-0.5 text-xs font-semibold">
                  {r.id}
                </span>
              </div>
              <div className="col-span-7 flex items-start gap-3">
                <div>
                  <div className="flex items-center gap-2">

                    <span className="font-medium">{r.title}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{r.description}</p>
                </div>
              </div>

              <div className="col-span-2">
                <RuleParamEditor organisationRule={organisationRules[r.id]} rule={r} onChange={(patch) => updateRule(r.id, { param: { ...r.param, ...patch } })} />
              </div>

              <div className="col-span-2 flex items-center justify-end gap-2 mr-2 pt-1">
                <Toggle enabled={organisationRules[r.id]?.enabled} onChange={(v) => updateRule(r.id, { enabled: v })} />
              </div>

            </div>

            {r.list && r.list.length > 0 && (
              <div className="grid grid-cols-12 col-span-12  mt-4">
                <div className="col-span-1 items-center justify-center" />
                <div className="col-span-11 float-right ml-3 mb-1 border bg-violet-50 mr-2 px-2 pt-2 rounded-md">
                  {r.list.map((item, idx) => (
                    <span key={idx} className="inline-block bg-violet-200 text-violet-800 text-xs px-2 py-1 rounded-full mr-2 mb-2">{item}</span>
                  ))}
                </div>
              </div>
            )}

          </li>
        ))}

        {filtered.length === 0 && (
          <li className="px-4 py-12 text-center text-sm text-gray-500">No rules found.</li>
        )}
      </ul>
    </div>
  );
};

function Toggle({ enabled, onChange }) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={classNames(
        "relative inline-flex h-6 w-12 items-center rounded-full transition border-violet-800 border",
        enabled ? "bg-violet-800" : "bg-white"
      )}
      aria-pressed={enabled}
      aria-label={enabled ? "Disable" : "Enable"}
    >
      <span
        className={classNames(
          "inline-block h-5 w-5 transform rounded-full transition",
          enabled ? "translate-x-6 bg-white" : "translate-x-0.5 bg-violet-800"
        )}
      />
    </button>
  );
}

function RuleParamEditor({ organisationRule, rule, onChange }) {
  const p = rule.param || { type: "none" };

  if (rule.type === "none" || rule.type === 'boolean') return <span className="text-sm text-gray-400">—</span>;
  if (rule.type === "number")
    return (
      <div className="flex items-center gap-2 pt-2">
        <label className="text-xs w-24 text-violet-800">{rule.input_label}</label>
        <input
          type="number"
          className="w-28 rounded-xl border border-gray-200 px-2 py-1 text-sm"
          min={rule.min ?? 0}
          max={rule.max ?? 999}
          value={organisationRule.value}
          onChange={(e) => onChange({ value: Number(e.target.value || 0) })}
        />
      </div>
    );
  if (p.type === "text")
    return (
      <div className="flex items-center gap-2 pt-2">
        <label className="text-xs w-24 text-violet-800">{rule.input_label}</label>
        <input
          type="text"
          className="w-52 rounded-xl border border-gray-200 px-2 py-1 text-sm"
          value={organisationRule.value}
          onChange={(e) => onChange({ value: e.target.value })}
        />
      </div>
    );
  return null;
}

export default function FraudRulesManager({ onChange }) {
  const { enums } = useSelector((state) => state.enums);
  const [rules, setRules] = useState(() => enums?.shield?.rules || []);
  const [organisationRules, setOrganisationRules] = useState({});
  const [organisationRulesRaw, setOrganisationRulesRaw] = useState({});
  const [loading, setLoading] = useState(true);

  const { organisation } = useSelector((state) => state);

  useEffect(() => {
    // Fetch rules from API and store in local state
    async function fetchRules() {
      setLoading(true);

      const user = await Auth.currentAuthenticatedUser();

      const { data } = await Axios(
        API_ROUTES.user['organisations:shield-rules'](organisation?.organisation),
        {
          method: 'post',
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );

      const orgRules = {};
      data.data.rules.forEach(element => {
        orgRules[element.id] = element;
      });

      setOrganisationRules(orgRules);
      setOrganisationRulesRaw(data.data.rules);

      setLoading(false);
    }
    fetchRules();
  }, []);

  // Persist + notify
  useEffect(() => {
    localStorage.setItem("fraud_rules_v1", JSON.stringify(rules));
    onChange?.(rules);
  }, [rules, onChange]);

  async function updateRule(id, patch) {
    const ruleToUpdate = organisationRulesRaw.find((r) => r.id === id);
    if (patch.enabled !== undefined) ruleToUpdate.enabled = patch.enabled;
    if (patch.param?.value !== undefined) ruleToUpdate.value = patch.param.value;

    setOrganisationRules((prev) => {
      const updated = {
        ...prev,
        [id]: {
          ...prev[id],
          enabled: patch.enabled !== undefined ? patch.enabled : prev[id]?.enabled,
          value: patch.param?.value !== undefined ? patch.param.value : prev[id]?.value,
        },
      };
      return updated;
    });

    setOrganisationRulesRaw((prev) => {
      const updated = prev.map((r) => {
        if (r.id === id) {
          return {
            ...r,
            ...patch,
          };
        }

        return r;
      });
      return updated;
    });

    const user = await Auth.currentAuthenticatedUser();

    await Axios(
      API_ROUTES.user['organisations:shield-rules'](organisation?.organisation),
      {
        method: 'put',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        data: { ...ruleToUpdate },
      }
    );
  }

  if (!enums?.shield?.rules) return null;

  if (loading) {
    return (
      <div>
        <Head title="Fraud Shield Configuration" />
        <Skeleton count={1} height="100px" width="300px" />
        <div className="mt-4">
          {CATEGORIES.map((c) => (
            <RulesSection key={c.id} rules={rules} updateRule={updateRule} category={c.id} loading={true} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Head title="Fraud Shield Configuration" />

      {/* Create block with enabled rules count */}
      <Row className="mb-2">
        <Col md="12" lg="4" className="mb-3">
          <StatBox
            title={<span className='text-violet-800'><strong>Enabled</strong> Rules</span>}
            figure={`${organisationRulesRaw.filter(r => r.enabled).length} / ${rules.length}`}
            loading={false}
          />
        </Col>
      </Row>

      {CATEGORIES.map((c) => (
        <RulesSection key={c.id} rules={rules} updateRule={updateRule} category={c.id} loading={loading} organisationRules={organisationRules} />
      ))}
    </div>
  );
}